import { Copy, Trash, Users } from 'react-feather';
import { DocumentNode } from 'graphql';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import {
  GetTicketQuery as GetTicket,
  GetTicketsQuery as GetTickets,
  PurchaseStatus,
} from '__generated__/graphql';

import { Option } from '../../../../common/Common/OptionsDropdown';
import { encodeUrl, mergeUrlParams } from '../../../../common/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import { useSellableStatus } from '../../../Common/SellableStatusIndicator';
import ActivateTicketMutation from './ActivateTicketMutation';
import CardPreview from '../CardPreview';
import DeactivateTicketMutation from './DeactivateTicketMutation';
import DeleteTicketMutation from './DeleteTicketMutation';
import DuplicateTicketMutation from './DuplicateTicketMutation';
import EditTicketForm from './EditTicketForm';
import GetTicketsQuery from './GetTicketsQuery';
import PriceRange from '../../../../common/Common/PriceRange';
import SportIcon from '../../../../common/Common/SportIcon';
import StartDate from '../../../../common/Common/StartDate';
import UI from '../../../../common/UI';
import useConfirmModal from '../../../../common/useConfirmModal';
import useLocale from '../../../../common/useLocale';
import useMediaDevice from '../../../../common/useMediaDevice';
import useMutation from '../../../../api/useMutation';
import useProject from '../../../useProject';

type Event = GetTickets['project']['event'];
type TicketCategory = Event['ticket_categories'][0];
type Ticket = TicketCategory['tickets'][0];

export interface TicketListProps {
  event: Event;
  ticketCategory: TicketCategory;
  activeTicketId?: string;
  ticketData?: GetTicket;
  openAddTicketModal: () => void;
  refetchQueries?: DocumentNode[];
}

const TicketList = ({
  event, ticketCategory, activeTicketId, ticketData, openAddTicketModal,
  refetchQueries = [GetTicketsQuery],
}: TicketListProps) => {
  const { t } = useTranslation();
  const { formatInteger } = useLocale();
  const project = useProject();
  const device = useMediaDevice();

  return (
    <>
      <UI.HR />
      <UI.GridContainer sc={{ columns: '0px 6fr 4fr 4fr 3fr 34px 0px', gutter: 0.5, alignVertical: 'center' }}>
        <UI.FlexContainer
          sc={{ alignItems: 'center' }}
          style={{ gridColumn: device.isSmall ? '1 / span 4' : '1 / span 3' }}
        >
          <UI.Badge
            sc={{ size: 42, mr: 2, brand: 'gray.100', borderRadius: 'lg' }}
            style={{ boxShadow: 'inset 0 -40px 40px -20px rgba(0, 0, 0, 0.075)' }}
          >
            <UI.Icon sc={{ size: 2, color: 'gray.800' }}>
              <SportIcon type={ticketCategory.sport_type} />
            </UI.Icon>
          </UI.Badge>
          <UI.Div>
            <UI.H3>
              {ticketCategory.title}
            </UI.H3>
            {ticketCategory.start_date && (
              <UI.Span sc={{ color: 'gray.500', small: [true, false] }}>
                <StartDate date={ticketCategory.start_date} />
              </UI.Span>
            )}
          </UI.Div>
        </UI.FlexContainer>
        {!device.isSmall && (
          <UI.Div>
            <UI.TooltipContainer sc={{ inlineBlock: true, padding: 1, margin: -1 }}>
              <UI.Link
                to={`/${project.id}/participants?${encodeUrl({
                  event: event.id,
                  ticket_categories: [ticketCategory.id],
                  statuses: [PurchaseStatus.complete, PurchaseStatus.incomplete],
                })}`}
                sc={{ secondary: true }}
              >
                <UI.Icon>
                  <Users />
                </UI.Icon>
                {` ${formatInteger(ticketCategory.sold_count)} / ${ticketCategory.capacity !== null ? formatInteger(ticketCategory.capacity) : '∞'}`}
              </UI.Link>
              <UI.Tooltip sc={{ alignH: 'center' }}>
                {t('participants')}
              </UI.Tooltip>
            </UI.TooltipContainer>
          </UI.Div>
        )}
        <UI.FlexContainer
          sc={{ justifyContent: 'end', alignItems: 'center', color: 'gray.500' }}
          style={{ gridColumn: '5 / span 3' }}
        >
          <UI.LinkButton
            to={`/${project.id}/events/${event.id}/tickets/categories/${ticketCategory.id}`}
            sc={{ blank: true, size: 'sm' }}
          >
            {device.isSmall ? t('edit') : t('edit_ticket_category')}
          </UI.LinkButton>
        </UI.FlexContainer>
      </UI.GridContainer>
      <UI.GridContainer sc={{ gutter: 1 / 3 }}>
        {ticketCategory.tickets.map((ticket) => (
          <UI.CollapsibleNavCard
            to={`/${project.id}/events/${event.id}/tickets/${activeTicketId !== ticket.id ? ticket.id : ''}`}
            open={ticket.id === activeTicketId}
            muted={!!activeTicketId && ticket.id !== activeTicketId}
            preview={(
              <TicketPreview
                ticket={ticket}
                ticketCategory={ticketCategory}
                event={event}
                refetchQueries={refetchQueries}
              />
            )}
            body={ticketData
              ? (
                <EditTicketForm
                  ticket={ticketData.ticket}
                  event={ticketData.event}
                  distanceUnits={ticketData.distanceUnits}
                />
              )
              : <UI.PageLoader />}
            id={`TicketCard_${ticket.id}`}
            key={ticket.id}
          />
        ))}
        {ticketCategory.tickets.length === 0 && (
          <UI.Card sc={{ padding: 8, background: 'gray.50', color: 'gray.500', textAlign: 'center' }}>
            <UI.Div sc={{ maxWidth: [undefined, '60%'] }} style={{ margin: '0 auto' }}>
              <UI.H4 sc={{ mb: 1 }}>
                {t('no_tickets_yet')}
              </UI.H4>
              <Trans i18nKey="empty_ticket_category_actions">
                <UI.Link
                  to={mergeUrlParams({ ticket_category: ticketCategory.id })}
                  onClick={() => openAddTicketModal()}
                />
                <UI.Link to={`/${project.id}/events/${event.id}/tickets/sort`} />
              </Trans>
            </UI.Div>
          </UI.Card>
        )}
      </UI.GridContainer>
    </>
  );
};

interface TicketPreviewProps {
  ticket: Ticket;
  ticketCategory: TicketCategory;
  event: Event;
  refetchQueries?: DocumentNode[];
}

const TicketPreview = ({ ticket, ticketCategory, event, refetchQueries = [] }: TicketPreviewProps) => {
  const { t } = useTranslation();
  const { formatInteger } = useLocale();
  const notifier = useNotifier();
  const project = useProject();
  const history = useHistory();

  const [activateTicket] = useMutation(ActivateTicketMutation, {
    variables: {
      id: ticket.id,
    },
    optimisticResponse: {
      activateTicket: {
        id: ticket.id,
        active: true,
        __typename: 'Ticket',
      },
    },
    onCompleted: () => notifier.success(t('notifications.ticket_activated')),
  });

  const [deactivateTicket] = useMutation(DeactivateTicketMutation, {
    variables: {
      id: ticket.id,
    },
    optimisticResponse: {
      deactivateTicket: {
        id: ticket.id,
        active: false,
        __typename: 'Ticket',
      },
    },
    onCompleted: () => notifier.success(t('notifications.ticket_deactivated')),
  });

  const toggleStatus = () => {
    if (ticket.active) {
      deactivateTicket();
    } else {
      activateTicket();
    }
  };

  const [duplicateTicket, { loading: duplicating }] = useMutation(DuplicateTicketMutation, {
    variables: {
      id: ticket.id,
    },
    onCompleted: () => {
      notifier.success(t('notifications.ticket_duplicated'));

      history.push(`/${project.id}/events/${event.id}/tickets`);
    },
    onError: () => {
      notifier.error(t('something_went_wrong'));
    },
    refetchQueries,
    awaitRefetchQueries: refetchQueries.length > 0,
  });

  const [deleteTicket, { loading: deleting }] = useMutation(DeleteTicketMutation, {
    variables: {
      id: ticket.id,
    },
    onCompleted: () => {
      notifier.success(t('notifications.ticket_deleted'));

      history.push(`/${project.id}/events/${event.id}/tickets`);
    },
    onError: () => {
      notifier.error(t('something_went_wrong'));
    },
    refetchQueries,
    awaitRefetchQueries: refetchQueries.length > 0,
  });

  const [confirmDelete] = useConfirmModal({
    title: t('delete_name', { name: ticket.title }),
    onConfirm: () => deleteTicket(),
  }, [ticket]);

  const { message } = useSellableStatus(ticket);

  return (
    <CardPreview
      title={ticket.title}
      subTitle={ticket.units > 1 && (
        <UI.Label sc={{ small: true }}>
          <UI.Icon>
            <Users />
          </UI.Icon>
          {' '}
          {ticket.units}
        </UI.Label>
      )}
      firstColumn={(
        <UI.Span sc={{ color: 'gray.500' }}>
          <PriceRange prices={ticket.promotions.map(({ amount }) => ({ amount }))} />
        </UI.Span>
      )}
      secondColumn={(
        <>
          <UI.TooltipContainer sc={{ inlineBlock: true, padding: 1, margin: -1 }}>
            <UI.Link
              to={`/${project.id}/participants?${encodeUrl({
                event: event.id,
                tickets: [ticket.id],
                ticket_categories: [ticketCategory.id],
                statuses: [PurchaseStatus.complete, PurchaseStatus.incomplete],
              })}`}
              sc={{ secondary: true }}
            >
              <UI.Icon>
                <Users />
              </UI.Icon>
              {' '}
              {formatInteger(ticket.sold_count)}
              {' / '}
              {ticket.capacity !== null ? formatInteger(ticket.capacity) : '∞'}
            </UI.Link>
            <UI.Tooltip sc={{ alignH: 'center' }}>
              {t('participants')}
            </UI.Tooltip>
          </UI.TooltipContainer>
          {ticket.invitation_reservations_count > 0 && ` (${t('n_reservations', {
            count: ticket.invitation_reservations_count,
            value: formatInteger(ticket.invitation_reservations_count),
          })})`}
        </>
      )}
      status={message}
      active={ticket.active}
      onToggle={toggleStatus}
      options={() => (
        <>
          <Option
            icon={<Copy />}
            label={t('duplicate')}
            onClick={() => duplicateTicket()}
            disabled={duplicating}
          />
          {ticket.deletable && (
            <Option
              icon={<Trash />}
              label={t('delete')}
              onClick={() => confirmDelete()}
              disabled={deleting}
            />
          )}
        </>
      )}
    />
  );
};

export default TicketList;
